import * as braze from '@braze/web-sdk';
import { type FeatureFlag, type User } from '@braze/web-sdk';

interface IBrazeUtil {
	initialize(): void;

	init(userId: string): void;

	sendEvent(name: string, properties: any): boolean;

	setFirstName(name: string): boolean;

	setGender(name: typeof User.Genders): boolean;

	setDateOfBirth(year: number, month: number, day: number): boolean;

	setCustomUserAttribute(key: string, payload: any): boolean;

	getFeatureFlag(key: string): FeatureFlag;

	refreshFeatureFlags(success: () => void, error: () => void): void;

	registerFeatureFlagsUpdatesListener(subscriber: (featureFlags: FeatureFlag[]) => void): string;

	removeFeatureFlagsUpdatesListener(subscriptionId: string): void;
}

class BrazeUtilBase implements IBrazeUtil {
	static instance: BrazeUtilBase;

	constructor() {
		if (!BrazeUtilBase.instance) {
			BrazeUtilBase.instance = this;
		}

		return BrazeUtilBase.instance;
	}

	initialize(): void {
		braze.initialize(process.env.BRAZE_API_KEY!, {
			baseUrl: process.env.BRAZE_ENDPOINT!,
			enableLogging: process.env.MODE === 'dev',
			allowUserSuppliedJavascript: false
		});
	}

	init(userId: string): void {
		braze.automaticallyShowInAppMessages();

		braze.subscribeToContentCardsUpdates(function (cards) {
			console.log('braze cards:', cards);
		});

		braze.changeUser(userId);

		braze.openSession();

		if ('serviceWorker' in navigator) {
			navigator.serviceWorker
				.register('/service-worker.js')
				.then(registration => {
					console.log('Service Worker registered: ', registration);
				})
				.catch(error => {
					console.error('Service Worker registration failed: ', error);
				});

			navigator.serviceWorker.ready.then(registration => {
				// registration.showNotification("Hello! This is a test notification.").catch(console.error);

				braze.requestPushPermission(
					() => {
						console.log(`braze: You are registered for push!`);
					},
					() => {
						console.log(`braze: Something went wrong.`);
					}
				);
			});
		}
	}

	sendEvent(name: string, properties: any = undefined): boolean {
		return braze.logCustomEvent(name, properties);
	}

	setFirstName(name: string): boolean {
		return braze.getUser().setFirstName(name);
	}

	setGender(arg: typeof User.Genders): boolean {
		return braze.getUser().setGender(arg);
	}

	setDateOfBirth(year: number, month: number, day: number): boolean {
		return braze.getUser().setDateOfBirth(year, month, day);
	}

	setCustomUserAttribute(key: string, payload: any): boolean {
		return braze.getUser().setCustomUserAttribute(key, payload);
	}

	getFeatureFlag(key: string): FeatureFlag {
		return braze.getFeatureFlag(key);
	}

	refreshFeatureFlags(success: () => void, error: () => void): void {
		braze.refreshFeatureFlags(success, error);
	}

	registerFeatureFlagsUpdatesListener(subscriber: (featureFlags: FeatureFlag[]) => void): string {
		return braze.subscribeToFeatureFlagsUpdates(subscriber);
	}

	removeFeatureFlagsUpdatesListener(subscriptionId: string): void {
		braze.removeSubscription(subscriptionId);
	}
}

const BrazeUtil: IBrazeUtil = new BrazeUtilBase();

export default BrazeUtil;
